import { useState, useEffect, useRef, useCallback } from 'react';
import { apiFetch } from '../api';
import { fetchCache as cache, subscribeFetchCache } from '../utils/fetchCache';

// Module-level (not React state) so it survives a component unmount/remount
// — navigating away from a page and back no longer shows a loading flash
// and re-fetches from the network every time, which is what made clicking
// e.g. "Applikationer" repeatedly feel like it reloaded. Keyed by the full
// URL, so different tenants/query params naturally get separate entries —
// nothing to invalidate manually when switching tenants.

export function useCachedFetch(url, { skip } = {}) {
  const [data, setData] = useState(() => (url && cache.has(url) ? cache.get(url) : null));
  const [loading, setLoading] = useState(() => !!url && !cache.has(url));
  const [error, setError] = useState('');
  const cancelledRef = useRef(false);

  const load = useCallback(
    (force, silent) => {
      if (!url || skip) return;
      if (!force && cache.has(url)) {
        setData(cache.get(url));
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      setError('');
      apiFetch(url)
        .then((r) => r.json())
        .then((result) => {
          if (cancelledRef.current) return;
          if (result.error) {
            setError(typeof result.error === 'string' ? result.error : JSON.stringify(result.error));
          } else {
            cache.set(url, result);
            setData(result);
          }
        })
        .catch((err) => !cancelledRef.current && setError(err.message))
        .finally(() => !cancelledRef.current && setLoading(false));
    },
    [url, skip]
  );

  useEffect(() => {
    cancelledRef.current = false;
    load(false);
    return () => {
      cancelledRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, skip]);

  // A write to this resource elsewhere in the app: fetch a fresh copy quietly
  // (no loading flash), so a page that is still mounted never shows old data.
  useEffect(
    () =>
      subscribeFetchCache((root) => {
        if (url && !skip && url.startsWith(root)) load(true, true);
      }),
    [url, skip, load]
  );

  return { data, loading, error, refetch: () => load(true) };
}
